import { Fragment, useState } from 'react'

import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { useZipper } from '@hooks/useZipper'
import type { Entity } from '@models/Entity'
import { formatDateTime } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import type { BijlageTemplate } from '../../../templates/types/BijlageTemplate'
import ExpandRow from '../../../theme/components/ExpandRow'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { Bijlage } from '../types/Bijlage'

const expandedHeaderCells = [
  { id: 'naam', label: 'Naam', fixed: 500 },
  { id: 'created.date', label: 'Aangemaakt', fixed: 50 },
  { id: 'created.organisatie', label: 'Aangemaakt door', fixed: 150 },
  { id: 'opties', label: '', fixed: 180 },
] as const

interface Props {
  entity: Entity
  template: BijlageTemplate
  bijlagen: Bijlage[]
  user: AuthenticatedUser
  onOpen: (bijlage: Bijlage) => void
  onUpload: (documentsoort: string) => void
  onCancel: (bijlage: Bijlage) => void
}

export const AlgemeneBijlage = ({
  entity,
  template,
  bijlagen,
  user,
  onOpen,
  onUpload,
  onCancel,
}: Props) => {
  const { combi, organisatie } = user
  const [isExpanded, setIsExpanded] = useState(false)

  const templateBijlagen = bijlagen.filter(
    (bijlage) => bijlage.documentsoort === template.documentsoort,
  )
  const hasBijlagen = templateBijlagen.length > 0

  const fileName = `${entity.id}-${entity.projecttype}-${template.documentsoort}`
  const { loading, save } = useZipper(fileName)

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev)
  }

  return (
    <Fragment>
      <TableRow sx={{ backgroundColor: '#F7F7F7' }}>
        <TableCell>{template.documentsoort}</TableCell>
        <TableCell>
          {hasBijlagen && (
            <ExpandRow
              data-testid={`expandBijlagen-${template._id}`}
              expanded={isExpanded}
              onDelete={toggleExpanded}
              onClick={toggleExpanded}
            />
          )}
        </TableCell>
        <TableCell>
          <>
            <StyledIconButton
              title={`Bijlage uploaden voor ${template.documentsoort}`}
              onClick={() => onUpload(template.documentsoort)}
              icon={<UploadFileRoundedIcon />}
            />
            {templateBijlagen.length === 1 && (
              <StyledIconButton
                title="Bijlage openen"
                icon={<CloudDownloadIcon />}
                onClick={() => onOpen(templateBijlagen[0])}
              />
            )}

            {templateBijlagen.length > 1 &&
              templateBijlagen.find((b) => b.status !== 'cancelled') && (
                <StyledIconButton
                  title="Bijlagen downloaden"
                  icon={
                    loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CloudDownloadIcon />
                    )
                  }
                  onClick={() =>
                    save(
                      templateBijlagen
                        .filter((b) => b.status !== 'cancelled')
                        .map(
                          (b) =>
                            `/rest/${entity.type}/${entity.id}/bijlage/${b._id}`,
                        ),
                    )
                  }
                />
              )}
          </>
        </TableCell>
      </TableRow>

      {hasBijlagen && isExpanded && (
        <TableRow>
          <TableCell colSpan={3} sx={{ padding: 0, paddingRight: '2px' }}>
            <Table
              sx={{
                backgroundColor: '#f7f7f7',
                filter: 'brightness(0.95)',
                border: '2px dashed rgba(0,0,0,.25)',
              }}
            >
              <TableHead>
                <TableRow>
                  {expandedHeaderCells.map((headerCell) => (
                    <TableCell
                      key={headerCell.id}
                      sx={{ width: headerCell.fixed }}
                    >
                      {headerCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {templateBijlagen.map((bijlage) => {
                  const isCancelled = bijlage.status === 'cancelled'
                  const isCreator = bijlage.created.organisatie === organisatie
                  const canCancel = !isCancelled && (isCreator || combi)

                  return (
                    <TableRow
                      key={bijlage._id}
                      sx={{
                        backgroundColor: isCancelled
                          ? 'lightgray !important'
                          : '',
                      }}
                    >
                      <TableCell>
                        {`${bijlage.naam}${
                          isCancelled ? ' (geannuleerd)' : ''
                        }`}
                        <br />
                        <Typography variant="caption">
                          {bijlage.omschrijving}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDateTime(bijlage.created.date)}
                      </TableCell>
                      <TableCell>
                        {bijlage.created.organisatie}
                        {bijlage.created.email && (
                          <Typography variant="caption">
                            <br />
                            {bijlage.created.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StyledIconButton
                          title="Bijlage openen"
                          icon={<CloudDownloadIcon />}
                          onClick={() => onOpen(bijlage)}
                        />
                        {canCancel && (
                          <StyledIconButton
                            title="Bijlage annuleren"
                            icon={<DeleteIcon />}
                            onClick={() => onCancel(bijlage)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}
